
import React, { useState, useEffect, useCallback } from 'react';
import './Projects.css';
import project1 from '../assets/images/project1.png';
import project2 from '../assets/images/project2.png';
import project3 from '../assets/images/project3.png';
import project4 from '../assets/images/project4.jpeg';
import project5 from '../assets/images/project5.jpg';
import project6 from '../assets/images/project6.png';
import project7 from '../assets/images/project7.png';
import project8 from '../assets/images/project8.jpg';
import project9 from '../assets/images/project9.jpg';
import project10 from '../assets/images/project10.svg';

const Projects = () => {
    const [selectedProject, setSelectedProject] = useState(null);
    const [columnsCount, setColumnsCount] = useState(() => {
        if (typeof window === "undefined") return 4;
        const w = window.innerWidth;
        if (w >= 1200) return 4;
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        return 1;
    });

    // Lock background scroll while modal open
    useEffect(() => {
        if (selectedProject) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = prev;
            };
        }
    }, [selectedProject]);

    // Compute responsive columns
    const handleResize = useCallback(() => {
        const w = window.innerWidth;
        if (w >= 1200) setColumnsCount(4);
        else if (w >= 1024) setColumnsCount(3);
        else if (w >= 768) setColumnsCount(2);
        else setColumnsCount(1);
    }, []);

    useEffect(() => {
        let id = null;
        const onResize = () => {
            if (id) clearTimeout(id);
            id = setTimeout(() => {
                handleResize();
            }, 120);
        };
        window.addEventListener("resize", onResize);
        handleResize();
        return () => {
            window.removeEventListener("resize", onResize);
            if (id) clearTimeout(id);
        };
    }, [handleResize]);

    const projects = [
        {
            title: "Football Analytics System",
            short: "AI-driven system for automated player tracking and tactical analysis.",
            long: "Built an AI-driven football system to automate player tracking and tactical analysis, replacing manual video review. The system tracks players in real-time, collecting detailed metrics including distance covered, sprint speed, acceleration, deceleration, positional heatmaps, ball possession, pass completion, and tactical zone usage. It generates comprehensive player stats, performance reports, and visual dashboards, providing actionable insights for player development, match preparation, tactical decisions, and team strategy optimization.",
            tech: ["Python", "FastAPI", "Docker", "YOLO", "SAM", "ByteTrack/DeepSORT", "OpenCV", "Analytics", "AWS"],
            image: null,
        },
        {
            title: "CS:GO Computer Vision AI Bot",
            short: "Real-time object detection and tracking for in-game entities.",
            long: "Developed a Computer Vision–based AI system to detect and track in-game entities in CS:GO using real-time video frame analysis. Implemented object detection pipelines to identify players, movement patterns, and on-screen elements directly from gameplay footage. Deployed and optimized the system on a local ROCm-enabled server, enabling low-latency, GPU-accelerated real-time inference.",
            tech: ["Python", "OpenCV", "YOLO", "PyTorch", "MMDetection", "Kalman Filter", "ONNX", "TensorRT", "ROCm", "CUDA"],
            image: null,
        },
        {
            title: "3D Avatar Customer Service Agent",
            short: "RAG chatbot with 3D avatar animation and multilingual support.",
            long: "Developed a 3D avatar–based RAG chatbot for a gym, communicating in Indonesian using Komodo-7B. Implemented Generative AI pipelines for context-aware responses, interactive dialogue, and personalized guidance. Integrated TTS/STT functionality for voice-based interactions, enabling real-time conversational engagement. Designed real-time 3D avatar animation and lip-syncing for an immersive user experience, managing member inquiries and feedback in a scalable manner.",
            tech: ["PyTorch", "Hugging Face Transformers", "Komodo-7B", "Generative AI", "CUDA", "LLMs", "RAG", "TTS/STT"],
            image: null,
        },
        {
            title: "Mehfooz-E-Amm",
            short: "AI-powered personal safety app with live tracking, Dangerous Area Mapping & Accident Detection.",
            long: "Mehfooz-e-Amm provides live location sharing with trusted contacts, ensuring real-time safety tracking. It features high-risk area mapping using data analysis to alert users about potential dangers. The app integrates AI-based accident detection and alerts, enabling swift emergency response. Additionally, it includes a personal safety assistant for proactive security and a one-tap SOS trigger for instant help during emergencies.",
            tech: ["Kotlin", "Python", "Google Firebase", "Google Maps API", "OpenStreetMap API", "AI/ML Technologies", "Encryption", "FastAPI", "Docker"],
            image: project1,
        },
        {
            title: "Scoutrex (formerly Khiladi)",
            short: "An AI-powered cricket coaching app for fast bowling training.",
            long: "Scoutrex is an intelligent cricket coaching application focused on fast bowling training. The app leverages AI/ML models and generative AI to provide real-time feedback on bowling mechanics, posture, and speed. It uses computer vision techniques to analyze player movements, highlight performance gaps, and suggest corrective actions for improving accuracy, consistency, and injury prevention. Coaches and players can access personalized training sessions, progress tracking dashboards, and performance analytics. With seamless mobile integration, Scoutrex brings professional-level coaching to aspiring cricketers anytime, anywhere.",
            tech: ["Flutter", "Python", "FastAPI", "Docker", "AI/ML Technologies", "Generative AI", "TensorFlow", "PyTorch", "Supabase", "Google Cloud / AWS"],
            image: project2,
        },
        {
            title: "The Bloodstock Connection",
            short: "AI-powered biomechanics platform for horse gait and performance analysis.",
            long: "The Bloodstock Connection is an AI-driven application designed to revolutionize horse gait and performance evaluation. Leveraging YOLO for keypoint detection and tracking, alongside DeepLabCut and advanced machine learning models, the system analyzes stride length, symmetry, joint angles, and overall movement efficiency. These insights assist trainers, veterinarians, and breeders in monitoring performance, detecting early signs of lameness, and optimizing training strategies. Built with Flutter for seamless cross-platform use, the platform integrates AI/ML pipelines, FastAPI microservices, and cloud infrastructure to deliver accurate, real-time biomechanics analysis directly on mobile devices.",
            tech: ["Flutter", "Python", "FastAPI", "Docker", "YOLO", "DeepLabCut", "TensorFlow", "PyTorch", "OpenCV", "Scikit-learn", "NVIDIA CUDA", "Firebase", "Google Cloud / AWS"],
            image: project3,
        },
        {
            title: "English-Urdu Neural Machine Translation",
            short: "Fine-tuned MBART model for high-quality English ↔ Urdu translation.",
            long: "This project focuses on building a state-of-the-art Neural Machine Translation (NMT) system between English and Urdu. Using the MBART-50 multilingual model as the backbone, I fine-tuned it on a large parallel English-Urdu corpus. The pipeline included dataset preprocessing with KaggleHub, tokenization with Hugging Face, and training with the Seq2SeqTrainer API. Advanced techniques like label padding, gradient accumulation, and BOS token forcing were applied for stable training. The final model achieved a BLEU score of **53.21**, demonstrating strong translation quality. It supports real-time translation, providing accurate Urdu outputs for English inputs. Training loss and evaluation metrics were logged and visualized, while the model and tokenizer were saved for deployment in downstream applications such as chatbots, localization systems, or mobile apps.",
            tech: ["Python", "Transformers (Hugging Face)", "PyTorch", "MBART-50", "KaggleHub", "Datasets", "scikit-learn", "Matplotlib", "SacreBLEU"],
            image: project4,
        },
        {
            title: "FaceMorph AI",
            short: "CycleGAN Sketch-to-Face Translation.",
            long: "Implemented a CycleGAN-based model for translating sketches to real faces and vice versa. Built a Flask UI demo for interactive testing on the Person Face Sketches dataset.",
            tech: ["Python", "PyTorch", "Flask", "CycleGAN", "Computer Vision"],
            image: project5,
        },
        {
            title: "SkyMap GeoAI",
            short: "Automatic UAV Image Georeferencing System.",
            long: "Developed an Automatic Georeferencing System (AGS) for UAVs using local feature-based image matching. Users can click any pixel on a UAV image to retrieve its geodetic location by computing homography with a reference satellite image. Supports SIFT, ORB, and other keypoint detection methods.",
            tech: ["Python", "OpenCV", "Tkinter", "Computer Vision", "Geospatial Analysis"],
            image: project6,
        },
        {
            title: "DiceVision AI",
            short: "Real-Time Dice Game Using YOLO.",
            long: "Developed an interactive dice game powered by YOLO object detection. The system detects real dice faces in real-time using a webcam, automatically updating scores and enabling gameplay without any clicks or buttons. Pure computer vision fun!",
            tech: ["Python", "OpenCV", "YOLO", "Computer Vision", "Real-Time Detection"],
            image: project7,
        },
        {
            title: "Weather Application",
            short: "Real-time weather updates with animations.",
            long: "A cross-platform mobile weather application built with Flutter. The app integrates the OpenWeatherMap API to fetch live weather data for user-selected locations, displaying temperature, humidity, wind speed, and overall conditions. Interactive Rive animations enhance the user experience by visually representing weather states, while Flutter pub packages were used for smooth UI components and state management. The app ensures real-time updates and intuitive navigation for accurate and engaging weather reporting.",
            tech: ["Flutter", "OpenWeatherMap API", "Rive Animations", "Pub Packages", "REST API Integration"],
            image: project8,
        },
        {
            title: "TaskFlow",
            short: "Cross-Device To-do Application with Real-Time Sync.",
            long: "Developed a Task Managing Application with a centralized database using Google Firebase. Enabled real-time task management, synchronization across multiple devices, and smooth user interactions enhanced with Riverpod state management and Rive animations.",
            tech: ["Flutter", "Dart", "Firebase", "Riverpod", "Rive Animations"],
            image: project9,
        },
        {
            title: "FitSched AI",
            short: "Automated Gym Timetable & Management System.",
            long: "Developed a Gym Management System implementing automated scheduling using Python and AI/ML libraries. The system uses Java Swing for the user interface, applying OOP principles to streamline gym operations, member management, and timetable automation.",
            tech: ["Python", "Java", "AI/ML", "OOP", "Swing GUI"],
            image: project10
        }
    ];

    // Distribute projects into N columns
    const columns = Array.from({ length: columnsCount }, () => []);
    projects.forEach((p, i) => {
        columns[i % columnsCount].push({ ...p, _idx: i });
    });

    return (
        <section id="projects" className="section">
            <div className="projects-container">
                <div className="projects-heading-wrapper">
                    <h2>Projects</h2>
                </div>
                
                <div className="projects-grid-wrapper">
                    <div className="projects-grid">
                        {columns.map((col, colIndex) => (
                            <div className="projects-column" key={`col-${colIndex}`}>
                                {col.map((project) => (
                                    <div
                                        key={project.title + "-" + project._idx}
                                        className="project-card"
                                        onClick={() => setSelectedProject(project)}
                                    >
                                        <h3>{project.title}</h3>

                                        <div className="desc-wrap">
                                            <p className="short-desc">{project.short}</p>
                                            <p className="view-more-text">Click to view more →</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Modal */}
            {selectedProject && (
                <div
                    className="project-modal-overlay"
                    onClick={() => setSelectedProject(null)}
                >
                    <div
                        className="project-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="project-modal-close"
                            onClick={() => setSelectedProject(null)}
                        >
                            ✕
                        </button>

                        <div className="project-modal-content">
                            <div className="project-modal-text">
                                <h2>{selectedProject.title}</h2>
                                <p>{selectedProject.long}</p>

                                <div className="tech-stack">
                                    <strong>Technologies Used:</strong>
                                    <ul>
                                        {selectedProject.tech.map((t, i) => (
                                            <li key={i}>{t}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {selectedProject.image && (
                                <div className="project-modal-image">
                                    <img
                                        src={selectedProject.image}
                                        alt={selectedProject.title}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Projects;
